import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AdminFeaturesService } from '../service/admin-features.service';

type Severity = 'info' | 'warning' | 'critical';
type Audience = 'all' | 'schools' | 'students';

/** Ready-made wording for the usual incidents; the admin can still edit it. */
const PRESETS: { label: string; severity: Severity; message: string }[] = [
  {
    label: 'System slow / errors',
    severity: 'warning',
    message: 'Some pages are slow or showing errors right now. Our team is working on it - please try again in a little while.',
  },
  {
    label: 'Payments failing',
    severity: 'critical',
    message: 'Online payments are temporarily not going through. Please do not retry repeatedly - we will post here once it is fixed. Any amount deducted will be settled automatically.',
  },
  {
    label: 'Planned maintenance',
    severity: 'info',
    message: 'The ISSO portal will be under maintenance today from __ to __. Please save your work before then.',
  },
];

@Component({
  selector: 'app-site-notice',
  templateUrl: './site-notice.component.html',
  styleUrls: ['./site-notice.component.css'],
  providers: [MessageService],
})
export class SiteNoticeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = true;
  saving = false;
  presets = PRESETS;

  active = false;
  message = '';
  severity: Severity = 'warning';
  audience: Audience = 'all';
  /** yyyy-MM-ddTHH:mm for <input type="datetime-local">, blank = no auto-off. */
  endsAt = '';
  updatedAt: string | null = null;
  /** What is live right now, to tell the admin whether Save changes anything. */
  liveActive = false;

  constructor(private svc: AdminFeaturesService, private toast: MessageService) {}

  ngOnInit() { this.load(); }
  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }

  load() {
    this.loading = true;
    this.svc.siteNotice().pipe(takeUntil(this.destroy$)).subscribe(
      (n: any) => {
        this.active = !!n.active;
        this.liveActive = !!n.active;
        this.message = n.message || '';
        this.severity = n.severity || 'warning';
        this.audience = n.audience || 'all';
        this.endsAt = n.endsAt ? this.toLocalInput(n.endsAt) : '';
        this.updatedAt = n.updatedAt || null;
        this.loading = false;
      },
      () => { this.loading = false; this.err('Could not load the site notice'); },
    );
  }

  usePreset(p: { severity: Severity; message: string }) {
    this.severity = p.severity;
    this.message = p.message;
  }

  save(active: boolean) {
    const message = this.message.trim();
    if (active && !message) { this.err('Enter the message to show'); return; }
    this.saving = true;
    const body = {
      active,
      message,
      severity: this.severity,
      audience: this.audience,
      endsAt: this.endsAt ? new Date(this.endsAt).toISOString() : null,
    };
    this.svc.saveSiteNotice(body).pipe(takeUntil(this.destroy$)).subscribe(
      (n: any) => {
        this.saving = false;
        this.active = !!n.active;
        this.liveActive = !!n.active;
        this.updatedAt = n.updatedAt || null;
        this.ok(active ? 'Notice is live. Open pages pick it up within a minute.' : 'Notice switched off.');
      },
      (e: any) => { this.saving = false; this.err((e && e.error && e.error.error) || 'Could not save the notice'); },
    );
  }

  fmt(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  private toLocalInput(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  private ok(m: string)  { this.toast.add({ key: 'custom', severity: 'success', summary: m, life: 6000 }); }
  private err(m: string) { this.toast.add({ key: 'custom', severity: 'error', summary: m, life: 6000 }); }
}
